// CookingFailure.jsx
import React from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  Button,
  Alert,
  Paper,
  Divider,
  Fade,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SettingsIcon from "@mui/icons-material/Settings";
import SignalWifiOffIcon from "@mui/icons-material/SignalWifiOff";
import HelpIcon from "@mui/icons-material/Help";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { keyframes } from "@mui/material";

const shake = keyframes`
  0% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  50% { transform: translateX(5px); }
  75% { transform: translateX(-5px); }
  100% { transform: translateX(0); }
`;

export const CookingFailure = ({
  status,
  recipe,
  resetStatus,
  onReset,
  onNavigateBack,
  isMergedRecipes,
}) => {
  const theme = useTheme();

  const messages = {
    failed: {
      title: "Cooking Halted",
      description:
        "The cooking process encountered an unexpected error and had to be stopped for safety reasons.",
      icon: <ErrorOutlineIcon fontSize="inherit" />,
      color: theme.palette.error.main,
      troubleshooting: [
        "Check if all ingredients were properly added",
        "Verify that the kitchen device is properly connected",
        "Make sure power supply is stable",
      ],
    },
    timeout: {
      title: "Connection Lost",
      description:
        "We've lost communication with your smart kitchen device. This could be due to network issues or device power problems.",
      icon: <SignalWifiOffIcon fontSize="inherit" />,
      color: theme.palette.warning.main,
      troubleshooting: [
        "Check your WiFi connection",
        "Ensure the kitchen device is powered on",
        "Try moving closer to reduce interference",
      ],
    },
  };

  const { title, description, icon, color, troubleshooting } =
    messages[status] || messages.failed;

  return (
    <Fade in timeout={800}>
      <Box
        sx={{
          minHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background:
            theme.palette.mode === "dark"
              ? `radial-gradient(circle, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`
              : `radial-gradient(circle, ${theme.palette.grey[50]} 0%, ${theme.palette.background.default} 100%)`,
          py: 8,
          px: 2,
        }}
      >
        <Paper
          elevation={4}
          sx={{
            maxWidth: 800,
            width: "100%",
            borderRadius: 4,
            overflow: "hidden",
            position: "relative",
            borderTop: `8px solid ${color}`,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "200px",
              opacity: 0.05,
              background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
              zIndex: 0,
            }}
          />

          <Box sx={{ p: { xs: 3, md: 5 }, position: "relative", zIndex: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                mb: 4,
              }}
            >
              <Box
                sx={{
                  fontSize: 100,
                  color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 3,
                  filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.15))",
                }}
              >
                {status === "timeout" ? (
                  <AccessTimeIcon
                    sx={{
                      fontSize: 120,
                      color: theme.palette.warning.main,
                      filter: `drop-shadow(0 4px 8px ${
                        theme.palette.mode === "dark"
                          ? "rgba(0,0,0,0.3)"
                          : "rgba(0,0,0,0.15)"
                      })`,
                    }}
                  />
                ) : (
                  <ErrorOutlineIcon
                    sx={{
                      fontSize: 120,
                      color: theme.palette.error.main,
                      filter: `drop-shadow(0 4px 8px ${
                        theme.palette.mode === "dark"
                          ? "rgba(0,0,0,0.3)"
                          : "rgba(0,0,0,0.15)"
                      })`,
                    }}
                  />
                )}
              </Box>

              <Typography
                variant="h3"
                sx={{
                  mb: 2,
                  fontWeight: 700,
                  color:
                    status === "timeout"
                      ? theme.palette.warning.main
                      : theme.palette.error.main,
                  textAlign: "center",
                }}
              >
                {status === "timeout" ? "Connection Timeout" : "Cooking Failed"}
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  mb: 4,
                  textAlign: "center",
                  maxWidth: 600,
                  px: 2,
                  color: "text.secondary",
                }}
              >
                {status === "timeout"
                  ? "We've lost communication with your smart kitchen device. This could be due to network issues or device power problems."
                  : "The cooking process encountered an unexpected error and had to be stopped for safety reasons."}
              </Typography>

              <Divider sx={{ width: "50%", my: 3 }} />

              <Box sx={{ width: "100%", maxWidth: 500, mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  Troubleshooting steps:
                </Typography>
                <List>
                  {troubleshooting.map((step, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            backgroundColor: `${color}20`,
                            color,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 600,
                            fontSize: "0.8rem",
                          }}
                        >
                          {index + 1}
                        </Box>
                      </ListItemIcon>
                      <ListItemText primary={step} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                justifyContent: "center",
                mb: 3,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                startIcon={<ReplayIcon />}
                onClick={onNavigateBack}
                size="large"
                sx={{
                  px: 3,
                  py: 1.2,
                  borderRadius: 3,
                  boxShadow: 2,
                  fontWeight: 600,
                }}
              >
                Try Again
              </Button>

              <Button
                variant="outlined"
                color="secondary"
                startIcon={
                  resetStatus.loading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <RestartAltIcon />
                  )
                }
                onClick={onReset}
                disabled={resetStatus.loading}
                size="large"
                sx={{
                  px: 3,
                  py: 1.2,
                  borderRadius: 3,
                  borderWidth: 2,
                  fontWeight: 600,
                }}
              >
                {resetStatus.loading ? "Resetting..." : "Reset System"}
              </Button>

              <Button
                variant="outlined"
                color="inherit"
                startIcon={<ArrowBackIcon />}
                onClick={() => window.history.back()}
                size="large"
                sx={{
                  px: 3,
                  py: 1.2,
                  borderRadius: 3,
                  borderWidth: 2,
                  fontWeight: 600,
                }}
              >
                Go Back
              </Button>
            </Box>

            {resetStatus.error && (
              <Alert
                severity="error"
                sx={{
                  mt: 2,
                  mb: 3,
                  borderRadius: 2,
                  boxShadow: 1,
                }}
              >
                <Typography variant="body2" fontWeight={500}>
                  Reset failed: {resetStatus.error}
                </Typography>
              </Alert>
            )}

            {resetStatus.tried && !resetStatus.error && (
              <Alert
                severity="success"
                sx={{
                  mt: 2,
                  mb: 3,
                  borderRadius: 2,
                  boxShadow: 1,
                }}
              >
                <Typography variant="body2" fontWeight={500}>
                  System reset successful. Try cooking again.
                </Typography>
              </Alert>
            )}

            <Divider sx={{ my: 3 }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Button
                startIcon={<ArrowBackIcon />}
                variant="text"
                color="inherit"
                onClick={onNavigateBack}
                sx={{ color: "text.secondary" }}
              >
                Back to Recipe
              </Button>

              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  startIcon={<SettingsIcon />}
                  variant="text"
                  color="inherit"
                  sx={{ color: "text.secondary" }}
                  onClick={() => {}}
                >
                  Device Settings
                </Button>
                <Button
                  startIcon={<HelpIcon />}
                  variant="text"
                  color="inherit"
                  sx={{ color: "text.secondary" }}
                  onClick={() => {}}
                >
                  Get Help
                </Button>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Fade>
  );
};
